import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthenticatedQuery } from '@/hooks/auth/use-auth-query-hooks';
import * as Logger from '@/lib/monitoring/logger';
import { accountKeys } from '@/features/workspace/api/account-keys';
import { updateAccountInCache } from '@/features/workspace/api/update-account-in-cache';
import { executeWorkspaceSwitch } from '@/features/workspace/api/execute-workspace-switch';
import { WorkspaceSwitchCommitPayload, type SwitchWorkspaceOptions } from '@/contexts/account/types/execute-workspace-switch-types';
import type { WorkspaceSwitchResult } from '@/contexts/account/types/workspace-switch-outcome-types';
import { useAnalyticsAccountSync } from '@/features/workspace/hooks/use-analytics-account-sync';
import { useAuthState } from '@/contexts/auth/auth-context';
import { AUTH_STATE } from '@/contexts/auth/types/auth-context-enums';
import { AccountStorage } from '@/features/workspace/api/account-storage/account-storage';
import { AccountsApiService } from '@/services/internal-api/accounts/accounts-api-service';
import useStore from '@/store/app/use-store';
import type { Role } from '@/services/internal-api/accounts/types/account-role-types';

import { AccountContextActions, AccountContextMeta, AccountContextState, AccountContextValue, OrderConfig } from '@/contexts/account/types/account-context-types';
import type { Account, Pixel } from '@adtrackify/at-tracking-event-types';
import { AccountContext } from '@/contexts/account/account-context';

interface AccountProviderProps {
  children: React.ReactNode;
}

const initialState: AccountContextState = {
  accountId: null,
  pixelId: null,
  currentAccount: null,
  currentPixel: null,
  accountsList: [],
  hasLoadedAccounts: false,
  accountsLoadFailed: false,
  isValidated: false,
  roles: [],
  orderConfig: {},
  shopifyCollections: [],
  shopifyTags: [],
};

const initialMeta: AccountContextMeta = {
  isTransitioning: false,
  previousAccountId: null,
  workspaceGeneration: 0,
};

const emptyAccountScopedState: Pick<AccountContextState, 'orderConfig' | 'shopifyCollections' | 'shopifyTags'> = {
  orderConfig: {},
  shopifyCollections: [],
  shopifyTags: [],
};

function resolveInitialAccountId(): string | null {
  if (typeof window === 'undefined') return null;

  const urlAid = new URL(window.location.href).searchParams.get('aid');
  if (urlAid) {
    const cachedData = AccountStorage.get(urlAid, 'account');
    if (cachedData?.currentAccount) {
      return urlAid;
    }
    // URL specifies an account we don't have cached — return null to wait for API
    // instead of falling back to a different cached account
    return null;
  }

  return AccountStorage.getLastAccountId();
}

function buildCachedInitialState(): AccountContextState {
  const accountId = resolveInitialAccountId();
  if (!accountId) return initialState;

  const cachedData = AccountStorage.get(accountId, 'account');
  if (!cachedData?.currentAccount) return initialState;

  const account = cachedData.currentAccount;
  const pixel = account.pixels?.[0] ?? null;

  Logger.debug('AccountProvider: initializing from cache', {
    accountId: account.id,
    pixelId: pixel?.id,
  });

  return {
    ...initialState,
    accountId: account.id,
    currentAccount: account,
    pixelId: pixel?.id ?? null,
    currentPixel: pixel,
    isValidated: false,
  };
}

export function AccountProvider({ children }: AccountProviderProps) {
  const [state, setState] = useState<AccountContextState>(buildCachedInitialState);
  const [meta, setMeta] = useState<AccountContextMeta>(initialMeta);

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const currentUserEmail = useStore(s => s.user?.email);

  // Sync account context to analytics service
  useAnalyticsAccountSync(state.currentAccount);

  // Keep latest state in a ref so switchWorkspace closes over stable deps
  // without stale accountsList/accountId/roles during rapid switches.
  const stateRef = useRef(state);
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Auto-fetch accounts when auth becomes 'authenticated'.
  // This ensures accounts are loaded for ALL routes (public + protected),
  // enabling downstream guards to make routing decisions.
  const { authState } = useAuthState();

  // Use React Query for deduplication and caching of user accounts fetch.
  // useAuthenticatedQuery automatically gates on valid auth token.
  // On error: keep hasLoadedAccounts false and set accountsLoadFailed so guards
  // do not treat an empty list as a successful "no accounts" result.
  const userAccountsQuery = useAuthenticatedQuery(
    accountKeys.list(),
    () => AccountsApiService.getUserAccounts(),
    {
      enabled: authState === AUTH_STATE.AUTHENTICATED && !state.hasLoadedAccounts,
      staleTime: Infinity,
      retry: 1,
      refetchOnWindowFocus: false,
    }
  );

  // Sync query outcome into account context state.
  useEffect(() => {
    if (userAccountsQuery.isSuccess && userAccountsQuery.data) {
      const accounts = userAccountsQuery.data.data.accounts ?? [];
      setState(s => {
        if (
          s.hasLoadedAccounts &&
          !s.accountsLoadFailed &&
          s.accountsList === accounts
        ) {
          return s;
        }

        // Update currentAccount from fresh list if it exists. This ensures
        // subscription status changes (e.g., cardless trial activation) are
        // reflected immediately in guards that check currentAccount.
        const updatedCurrentAccount = s.currentAccount
          ? accounts.find(acc => acc.id === s.currentAccount?.id) ?? s.currentAccount
          : s.currentAccount;

        return {
          ...s,
          accountsList: accounts,
          currentAccount: updatedCurrentAccount,
          hasLoadedAccounts: true,
          accountsLoadFailed: false,
        };
      });
      return;
    }

    if (userAccountsQuery.isError) {
      Logger.warn('Auto-fetch accounts failed', { error: userAccountsQuery.error });
      setState(s => {
        if (s.accountsLoadFailed && !s.hasLoadedAccounts) {
          return s;
        }
        return {
          ...s,
          accountsLoadFailed: true,
          hasLoadedAccounts: false,
        };
      });
    }
  }, [userAccountsQuery.isSuccess, userAccountsQuery.isError, userAccountsQuery.data, userAccountsQuery.error]);

  const refetchUserAccounts = userAccountsQuery.refetch;

  const retryLoadAccounts = useCallback(() => {
    setState(s => ({ ...s, accountsLoadFailed: false }));
    void refetchUserAccounts();
  }, [refetchUserAccounts]);

  const beginTransition = useCallback((previousAccountId: string | null) => {
    setMeta(m => ({
      ...m,
      isTransitioning: true,
      previousAccountId,
    }));
  }, []);

  const endTransition = useCallback(() => {
    setMeta(m => ({ ...m, isTransitioning: false }));
  }, []);

  const commitWorkspace = useCallback((payload: WorkspaceSwitchCommitPayload) => {
    setState(s => ({
      ...s,
      accountId: payload.accountId,
      currentAccount: payload.account,
      pixelId: payload.pixel?.id ?? null,
      currentPixel: payload.pixel,
      isValidated: true,
      ...emptyAccountScopedState,
    }));
    setMeta(m => ({ ...m, workspaceGeneration: m.workspaceGeneration + 1 }));
  }, []);

  const switchWorkspace = useCallback(
    async (
      workspaceId: string,
      options?: SwitchWorkspaceOptions
    ): Promise<WorkspaceSwitchResult> => {
      const latest = stateRef.current;
      return executeWorkspaceSwitch(workspaceId, {
        accountsList: options?.accounts ?? latest.accountsList,
        currentAccountId: latest.accountId,
        roles: latest.roles,
        currentUserEmail,
        queryClient,
        navigate,
        beginTransition,
        commitWorkspace,
        endTransition,
        onRequiresPlan: options?.onRequiresPlan,
        userId: options?.userId,
        navigateState: options?.navigateState,
        preserveLocation: options?.preserveLocation,
      });
    },
    [currentUserEmail, queryClient, navigate, beginTransition, commitWorkspace, endTransition]
  );

  const actions: AccountContextActions = useMemo(() => ({
    switchWorkspace,

    switchAccount: (accountId: string) => {
      void switchWorkspace(accountId);
    },

    setAccountsList: (accounts: Account[]) => {
      setState(s => ({
        ...s,
        accountsList: accounts,
        hasLoadedAccounts: true,
        accountsLoadFailed: false,
      }));
    },

    retryLoadAccounts,

    setCurrentAccount: (account: Account) => {
      // Defensive: handle null/undefined account
      if (!account) {
        Logger.warn('setCurrentAccount called with null/undefined account');
        return;
      }

      const pixel = account.pixels?.[0] ?? null;

      setState(s => ({
        ...s,
        ...(s.accountId === account.id ? {} : emptyAccountScopedState),
        currentAccount: account,
        accountId: account.id,
        currentPixel: pixel,
        pixelId: pixel?.id ?? null,
      }));

      if (account.id) {
        AccountStorage.switchAccount(account.id);
        AccountStorage.setLastAccountId(account.id);
        AccountStorage.set(account.id, 'account', {
          currentAccount: account,
          lastAccessedAt: Date.now(),
        });
      }
    },

    // Applies a saved change to an account without selecting it. A save can finish
    // after the user has switched away, so this must never move the session.
    updateAccount: (account: Account) => {
      if (!account?.id) {
        Logger.warn('updateAccount called without an account id');
        return;
      }

      const latest = stateRef.current;
      const existing = latest.currentAccount?.id === account.id
        ? latest.currentAccount
        : latest.accountsList.find(item => item.id === account.id);
      const merged: Account = { ...existing, ...account };

      setState(s => ({
        ...s,
        accountsList: s.accountsList.map(item => (item.id === merged.id ? merged : item)),
        currentAccount: s.accountId === merged.id ? merged : s.currentAccount,
      }));

      updateAccountInCache(queryClient, merged);
      AccountStorage.set(merged.id, 'account', {
        currentAccount: merged,
        lastAccessedAt: Date.now(),
      });
    },

    setCurrentPixel: (pixel: Pixel) => {
      setState(s => ({
        ...s,
        currentPixel: pixel,
        pixelId: pixel.id,
      }));
    },

    setRoles: (roles: Role[]) => {
      setState(s => ({ ...s, roles }));
    },

    getUserRolesForAccount: (accountId: string) => {
      const roleEntry = state.roles.find(role => role.accountId === accountId);
      return roleEntry?.role ?? null;
    },

    markValidated: () => {
      setState(s => ({ ...s, isValidated: true }));
    },

    setOrderConfig: (orderConfig: OrderConfig) => {
      setState(s => ({ ...s, orderConfig }));
    },

    setShopifyCollections: (collections) => {
      setState(s => ({ ...s, shopifyCollections: collections }));
    },

    setShopifyTags: (tags: string[]) => {
      setState(s => ({ ...s, shopifyTags: tags }));
    },

    clearAccountData: () => {
      queryClient.clear();

      AccountStorage.clearAll();

      setState(initialState);
      setMeta(initialMeta);
    },
  }), [state.roles, queryClient, switchWorkspace, retryLoadAccounts]);

  const contextValue = useMemo<AccountContextValue>(() => ({
    state,
    actions,
    meta,
  }), [state, actions, meta]);

  return (
    <AccountContext value={contextValue}>
      {children}
    </AccountContext>
  );
}
